import { execSQL, esc } from './api';

/* ------------------------------------------------------------------
 * Aggregate queries behind the Dashboard and Reports pages.
 * The API returns only the first result set, so anything that needs
 * several numbers at once is written as one row of scalar subqueries.
 * ------------------------------------------------------------------ */

// "Resigned" is derived from the resignation date, the same rule the
// employee list uses — the status column only marks soft-deletes.
/* One name everywhere. The payslip shows "Mrs.S.R Fernando"; the reports used to
 * print the full first name, so the same person read differently on a payslip and
 * on an EPF statement. Built from the employee master at read time so runs
 * calculated before this still come out in the new format. */
const NAME_SQL = (a) =>
  `LTRIM(RTRIM(ISNULL(${a}.initial,'') + ISNULL(${a}.initials,'') + ' ' + ISNULL(${a}.surName,'')))`;

/* The name the tax office knows. The Tax report and PAYE statement go to the
 * tax office, which has no titles and no dots: "S R Fernando", not
 * "Mrs.S.R Fernando". A TIN reference name, where someone registered under a
 * different name, is used exactly as typed. Every other report keeps NAME_SQL. */
const TIN_NAME_SQL = (a) => {
  // dots become spaces, then runs of spaces fold to one - over the whole name,
  // since a few surnames were keyed with a double space as well
  const squeeze = (s) => `REPLACE(REPLACE(REPLACE(${s}, '   ', ' '), '  ', ' '), '  ', ' ')`;
  const built = squeeze(`LTRIM(RTRIM(REPLACE(ISNULL(${a}.initials,''), '.', ' ') + ' ' + ISNULL(${a}.surName,'')))`);
  return `ISNULL(NULLIF(LTRIM(RTRIM(${a}.tinName)), ''), ${built})`;
};

/* Every report runs over a From-To range now, not a single month. A payroll run
 * is a whole month, so a range takes in each month it touches - 1 April to
 * 31 March is the tax year, 1 to 30 September is just September. */
export const rangeOf = (from, to) => ({
  from, to,
  fromMonth: String(from || '').slice(0, 7),
  toMonth:   String(to || '').slice(0, 7),
});

/* Run rows inside a range, from runs that have not been retired. The month is
 * taken from the run, not from each row: September's rows were once saved
 * labelled August, and filtering on the row label counted them into August
 * and left September empty. */
const RUN_ROWS = (r) =>
  `SELECT d.* FROM dbo.payroll_run_detail d ` +
  `INNER JOIN dbo.payroll_run rr ON rr.id = d.runId AND rr.status <> 'Deleted' ` +
  `WHERE rr.runMonth BETWEEN ${esc(r.fromMonth)} AND ${esc(r.toMonth)}`;

export const fetchDashboardStats = () =>
  execSQL(`
    SELECT
      (SELECT COUNT(*) FROM dbo.payroll_employee
        WHERE status <> 'Deleted'
          AND (dateOfResignation IS NULL OR dateOfResignation > GETDATE()))                     AS activeEmployees,
      (SELECT COUNT(*) FROM dbo.payroll_employee
        WHERE status <> 'Deleted'
          AND dateOfResignation IS NOT NULL AND dateOfResignation <= GETDATE())                 AS resignedEmployees,
      (SELECT COUNT(*) FROM dbo.payroll_leave    WHERE status = 'Pending')                      AS pendingLeave,
      (SELECT COUNT(*) FROM dbo.payroll_loan     WHERE status = 'Active')                       AS activeLoans,
      (SELECT ISNULL(SUM(remaining), 0) FROM dbo.payroll_loan WHERE status = 'Active')          AS loanOutstanding,
      (SELECT COUNT(*) FROM dbo.payroll_salary_component WHERE status <> 'Deleted')             AS componentCount
  `);

export const fetchRunSummary = (limit = 6) =>
  execSQL(`
    SELECT TOP ${Number(limit)} id, runMonth, status, employeeCount,
           totalGross, totalDeductions, totalEpfEmployee, totalEpfEmployer,
           totalEtf, totalNet, calculatedAt
    FROM dbo.payroll_run
    WHERE status <> 'Deleted'
    ORDER BY runMonth DESC
  `);

export const fetchPayrollSummaryReport = (r) =>
  execSQL(`
    SELECT runMonth, status, employeeCount, totalGross, totalDeductions,
           totalEpfEmployee, totalEpfEmployer, totalEtf, totalNet
    FROM dbo.payroll_run
    WHERE status <> 'Deleted'
      AND runMonth BETWEEN ${esc(r.fromMonth)} AND ${esc(r.toMonth)}
    ORDER BY runMonth DESC
  `);

/* Some staff are registered for tax under a different name from the one on their
 * EPF and bank - a maiden name, or the name on a newer NIC. The TIN reference
 * name holds that, and only this report uses it; everywhere else the EPF number
 * and the usual name stay the key. Blank falls back to the usual name. */
export const fetchTaxReport = (r) =>
  execSQL(`
    SELECT x.emp_id, ${TIN_NAME_SQL('e')} AS empName, e.tin,
           e.nic, ISNULL(NULLIF(e.designation,''), e.title) AS designation,
           x.grossPay, x.paye
    FROM (SELECT emp_id, SUM(grossPay) AS grossPay, SUM(paye) AS paye
          FROM (${RUN_ROWS(r)}) d GROUP BY emp_id HAVING SUM(paye) > 0) x
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = x.emp_id
    ORDER BY CAST(x.emp_id AS INT)
  `);

export const fetchEpfEtfReport = (r) =>
  execSQL(`
    SELECT x.emp_id, ${NAME_SQL('e')} AS empName, x.basicSalary, x.budgetaryAllowance, x.noPay,
           -- what the fund is contributed on: basic and budgetary, less any no-pay
           (x.basicSalary - x.noPay + x.budgetaryAllowance + x.increment) AS totalEarnings,
           x.epfEmployee, x.epfEmployer, x.etf
    FROM (SELECT emp_id,
                 SUM(ISNULL(basicSalary,0)) AS basicSalary, SUM(ISNULL(budgetaryAllowance,0)) AS budgetaryAllowance,
                 SUM(ISNULL(noPay,0)) AS noPay, SUM(ISNULL(increment,0)) AS increment,
                 SUM(epfEmployee) AS epfEmployee, SUM(epfEmployer) AS epfEmployer, SUM(etf) AS etf
          FROM (${RUN_ROWS(r)}) d GROUP BY emp_id
          HAVING SUM(epfEmployee) > 0 OR SUM(epfEmployer) > 0) x
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = x.emp_id
    ORDER BY CAST(x.emp_id AS INT)
  `);

// loans that had started by the end of the range
export const fetchLoanReport = (r) =>
  execSQL(`
    SELECT l.emp_id,
           ${NAME_SQL('e')} AS empName,
           l.loanType, l.principal, l.interestRate, l.totalPayable,
           l.installments, l.monthlyInstallment, l.remaining, l.status,
           CONVERT(varchar(10), l.startDate, 120) AS startDate
    FROM dbo.payroll_loan l
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = l.emp_id
    WHERE l.startDate IS NULL OR l.startDate <= ${esc(r.to)}
    ORDER BY l.status, CAST(l.emp_id AS INT)
  `);

// leave that overlaps the range at all
export const fetchLeaveReport = (r) =>
  execSQL(`
    SELECT lv.emp_id,
           ${NAME_SQL('e')} AS empName,
           lv.leaveType, lv.days, lv.status,
           CONVERT(varchar(10), lv.dateFrom, 120) AS dateFrom,
           CONVERT(varchar(10), lv.dateTo,   120) AS dateTo,
           lv.reason
    FROM dbo.payroll_leave lv
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = lv.emp_id
    WHERE lv.dateFrom <= ${esc(r.to)} AND lv.dateTo >= ${esc(r.from)}
    ORDER BY CAST(lv.emp_id AS INT), lv.id DESC
  `);

// over several months each figure is the employee's total for the range
export const fetchPayrollDetailReport = (r) =>
  execSQL(`
    SELECT x.*, ${NAME_SQL('e')} AS empName
    FROM (SELECT emp_id, MAX(category) AS category,
                 SUM(ISNULL(basicSalary,0)) AS basicSalary,
                 SUM(ISNULL(noPay,0)) AS noPay,
                 SUM(ISNULL(noPayDays,0)) AS noPayDays,
                 SUM(ISNULL(budgetaryAllowance,0)) AS budgetaryAllowance,
                 SUM(ISNULL(increment,0)) AS increment,
                 SUM(ISNULL(otherAllowance,0)) AS otherAllowance,
                 SUM(ISNULL(grossPay,0)) AS grossPay,
                 SUM(ISNULL(stampDuty,0)) AS stampDuty,
                 SUM(ISNULL(paye,0)) AS paye,
                 SUM(ISNULL(loanDeduction,0)) AS loanDeduction,
                 SUM(ISNULL(advanceDeduction,0)) AS advanceDeduction,
                 SUM(ISNULL(festivalAdvance,0)) AS festivalAdvance,
                 SUM(ISNULL(insuranceDeduction,0)) AS insuranceDeduction,
                 SUM(ISNULL(otherDeduction,0)) AS otherDeduction,
                 SUM(ISNULL(epfEmployee,0)) AS epfEmployee,
                 SUM(ISNULL(epfEmployer,0)) AS epfEmployer,
                 SUM(ISNULL(etf,0)) AS etf,
                 SUM(ISNULL(totalDeductions,0)) AS totalDeductions,
                 SUM(ISNULL(netPay,0)) AS netPay,
                 SUM(ISNULL(attendanceAllowance,0)) AS attendanceAllowance
          FROM (${RUN_ROWS(r)}) d GROUP BY emp_id) x
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = x.emp_id
    ORDER BY CAST(x.emp_id AS INT)
  `);

/* ---- Statutory statements ----------------------------------------
 * These match the client's own upload files column-for-column:
 *   EPF June 2026.xlsm                      (15 columns)
 *   ETF Contribution File Generator .xlsm   (8 columns)
 * Employer constants come from their files: EPF employer 22016 / zone A,
 * ETF employer "A 022016". Days worked is 30 less any no-pay days.
 * NIC is the fund-registered one — some staff have since been issued a new
 * NIC, but the funds still transfer against the number they hold. EPF and ETF
 * are registered separately and differ for a handful of members, so each
 * statement uses its own fund's details.
 * ------------------------------------------------------------------ */

export const EPF_EMPLOYER_NO = '22016';
export const EPF_ZONE        = 'A';
export const ETF_EMPLOYER_NO = 'A 022016';

// EPF upload format. Surname and initials are the EPF-registered ones,
// kept as separate columns exactly as the fund expects them.
export const fetchEpfStatement = (month) => {
  const period = String(month).replace('-', '');            // 2026-06 -> 202606
  return execSQL(`
    SELECT ISNULL(e.epfNic, e.nic)                 AS nicNumber,
           ISNULL(e.epfSurname, e.surName)         AS surname,
           ISNULL(e.epfInitials, e.initials)       AS initials,
           d.emp_id                                AS memberNumber,
           (d.epfEmployee + d.epfEmployer)         AS totalContribution,
           d.epfEmployer                           AS employerContribution,
           d.epfEmployee                           AS memberContribution,
           (ISNULL(d.basicSalary,0) - ISNULL(d.noPay,0)
              + ISNULL(d.budgetaryAllowance,0) + ISNULL(d.increment,0)) AS totalEarnings,
           ISNULL(e.epfMemberStatus, 'E')          AS memberStatus,
           '${EPF_ZONE}'                           AS zone,
           '${EPF_EMPLOYER_NO}'                    AS employerNumber,
           '${period}'                             AS contributionPeriod,
           1                                       AS dataSubmissionNumber,
           (30 - ISNULL(d.noPayDays, 0))           AS daysWorked,
           e.occupationGrade                       AS occupationGrade
    FROM dbo.payroll_run_detail d
    INNER JOIN dbo.payroll_run r ON r.id = d.runId
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = d.emp_id
    WHERE r.runMonth = ${esc(month)} AND r.status <> 'Deleted' AND d.epfEmployee > 0
    ORDER BY CAST(d.emp_id AS INT)
  `);
};

// ETF upload format. Member number is zero-padded to 6 digits and the
// contribution period is the first and last day of the month.
export const fetchEtfStatement = (month) => {
  const [y, m] = String(month).split('-').map(Number);
  const from = `${y}/${String(m).padStart(2, '0')}/01`;
  const last = new Date(y, m, 0).getDate();
  const to   = `${y}/${String(m).padStart(2, '0')}/${String(last).padStart(2, '0')}`;
  return execSQL(`
    SELECT ISNULL(e.etfNic, ISNULL(e.epfNic, e.nic))            AS nicNumber,
           ISNULL(e.etfSurname, ISNULL(e.epfSurname, e.surName))  AS surname,
           ISNULL(e.etfInitials, ISNULL(e.epfInitials, e.initials)) AS initials,
           RIGHT('000000' + CAST(d.emp_id AS VARCHAR(10)), 6) AS memberNumber,
           d.etf                             AS totalContribution,
           '${ETF_EMPLOYER_NO}'              AS employerNumber,
           '${from}'                         AS periodFrom,
           '${to}'                           AS periodTo
    FROM dbo.payroll_run_detail d
    INNER JOIN dbo.payroll_run r ON r.id = d.runId
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = d.emp_id
    WHERE r.runMonth = ${esc(month)} AND r.status <> 'Deleted' AND d.etf > 0
    ORDER BY CAST(d.emp_id AS INT)
  `);
};

export const fetchStampDutyStatement = (r) =>
  execSQL(`
    SELECT x.emp_id, ${NAME_SQL('e')} AS empName, x.category, x.grossPay, x.stampDuty
    FROM (SELECT emp_id, MAX(category) AS category, SUM(grossPay) AS grossPay, SUM(stampDuty) AS stampDuty
          FROM (${RUN_ROWS(r)}) d GROUP BY emp_id HAVING SUM(stampDuty) > 0) x
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = x.emp_id
    ORDER BY CAST(x.emp_id AS INT)
  `);

export const fetchPayeStatement = (r) =>
  execSQL(`
    SELECT x.emp_id, ${TIN_NAME_SQL('e')} AS empName, e.tin, e.nic, x.category, x.grossPay, x.paye
    FROM (SELECT emp_id, MAX(category) AS category, SUM(grossPay) AS grossPay, SUM(paye) AS paye
          FROM (${RUN_ROWS(r)}) d GROUP BY emp_id HAVING SUM(paye) > 0) x
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = x.emp_id
    ORDER BY CAST(x.emp_id AS INT)
  `);

/* ---- Analysis ---- */

// Current month vs the month before, per employee.
/* Nadee reconciles each deduction against its own schedule — insurance against
 * the insurer's list, festival advances against the advance register — so one
 * combined column was no use. Each kind gets its own report, listing only the
 * people who actually had it. */
const DEDUCTION_KINDS = {
  insurance: { column: 'insuranceDeduction', label: 'Insurance' },
  advance:   { column: 'advanceDeduction',   label: 'Salary Advance' },
  festival:  { column: 'festivalAdvance',    label: 'Festival Advance' },
  loan:      { column: 'loanDeduction',      label: 'Loan' },
  other:     { column: 'otherDeduction',     label: 'Other Deductions' },
};

export const fetchDeductionReport = (kind, r) => {
  const spec = DEDUCTION_KINDS[kind];
  if (!spec) return Promise.resolve([]);
  // just who and how much - Nadee checks these against the insurer's list and
  // the advance register, where gross and net only get in the way
  return execSQL(`
    SELECT x.emp_id, ${NAME_SQL('e')} AS empName, x.amount
    FROM (SELECT emp_id, SUM(ISNULL(${spec.column}, 0)) AS amount
          FROM (${RUN_ROWS(r)}) d GROUP BY emp_id
          HAVING SUM(ISNULL(${spec.column}, 0)) <> 0) x
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = x.emp_id
    ORDER BY CAST(x.emp_id AS INT)
  `);
};

/* Only the people whose gross moved. With everyone listed the handful of real
 * changes were lost among two hundred rows of zero. */
export const fetchMonthComparison = (month) => {
  const [y, m] = String(month).split('-').map(Number);
  const prevDate = new Date(y, m - 2, 1);
  const prev = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
  const one = (mm) => RUN_ROWS({ fromMonth: mm, toMonth: mm });
  return execSQL(`
    SELECT ISNULL(c.emp_id, p.emp_id)   AS emp_id,
           ISNULL(NULLIF(${NAME_SQL('e')}, ''), ISNULL(c.empName, p.empName)) AS empName,
           ISNULL(p.grossPay, 0)        AS prevGross,
           ISNULL(c.grossPay, 0)        AS currGross,
           (ISNULL(c.grossPay,0) - ISNULL(p.grossPay,0)) AS difference,
           ISNULL(p.netPay, 0)          AS prevNet,
           ISNULL(c.netPay, 0)          AS currNet
    FROM      (${one(month)}) c
    FULL JOIN (${one(prev)})  p ON c.emp_id = p.emp_id
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = ISNULL(c.emp_id, p.emp_id)
    WHERE ISNULL(c.grossPay,0) <> ISNULL(p.grossPay,0)
    ORDER BY CAST(ISNULL(c.emp_id, p.emp_id) AS INT)
  `);
};

// Per-employee allowance breakdown by component (Club & Societies, Sports, Form Teacher…).
export const fetchAllowanceBreakdown = () =>
  execSQL(`
    SELECT s.componentName,
           COUNT(*)              AS employeeCount,
           SUM(s.value)          AS totalAmount,
           AVG(s.value)          AS averageAmount
    FROM dbo.payroll_salary_structure s
    WHERE s.status <> 'Deleted' AND s.type = 'Allowance'
    GROUP BY s.componentName
    ORDER BY SUM(s.value) DESC
  `);

// Allowance totals grouped by school category.
export const fetchCategoryAllowanceReport = () =>
  execSQL(`
    SELECT ISNULL(e.category, 'Unassigned') AS category,
           COUNT(DISTINCT s.emp_id)         AS employees,
           SUM(s.value)                     AS totalAllowance
    FROM dbo.payroll_salary_structure s
    LEFT JOIN dbo.payroll_employee e ON e.emp_id = s.emp_id
    WHERE s.status <> 'Deleted' AND s.type = 'Allowance'
    GROUP BY e.category
    ORDER BY SUM(s.value) DESC
  `);

/* Contract staff with their age, for the start-of-year renewal review.
 * The client asked for this so contracts can be checked against age. */
export const fetchContractRenewalReport = () =>
  execSQL(`
    SELECT emp_id,
           ${NAME_SQL('e')} AS empName,
           category, employmentType,
           CONVERT(varchar(10), dob, 120)           AS dob,
           DATEDIFF(YEAR, dob, GETDATE())
             - CASE WHEN DATEADD(YEAR, DATEDIFF(YEAR, dob, GETDATE()), dob) > GETDATE()
                    THEN 1 ELSE 0 END               AS age,
           CONVERT(varchar(10), dateOfJoining, 120) AS dateOfJoining,
           basicSalary
    FROM dbo.payroll_employee e
    WHERE status <> 'Deleted'
      AND (dateOfResignation IS NULL OR dateOfResignation > GETDATE())
      AND employmentType <> 'Permanent'
    ORDER BY age DESC, CAST(emp_id AS INT)
  `);

/* ---------------- CSV export (client-side, no backend needed) ---------------- */

export const toCSV = (rows, columns) => {
  if (!rows?.length) return '';
  const cols = columns ?? Object.keys(rows[0]).map((k) => ({ key: k, label: k }));
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  /* Branch code 005 and account numbers with a leading zero are identifiers, not
   * quantities — Excel drops the zero the moment they look numeric. ="005" is the
   * one form Excel reliably keeps as text. */
  const cell = (c, v) => {
    if (!c.text) return escape(v);
    const t = v === null || v === undefined ? '' : String(v);
    return t === '' ? '' : `="${t.replace(/"/g, '""')}"`;
  };
  const head = cols.map((c) => escape(c.label)).join(',');
  const body = rows.map((r) => cols.map((c) => cell(c, r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
};

export const downloadCSV = (filename, rows, columns) => {
  const csv = toCSV(rows, columns);
  if (!csv) return false;
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
};
